import { useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { comics } from "@/mocks/comics";
import Breadcrumb from "@/components/base/Breadcrumb";

export default function ComicDetail() {
  const { id } = useParams<{ id: string }>();
  const comic = useMemo(
    () => comics.find((c) => c.id === Number(id)),
    [id]
  );

  if (!comic) {
    return (
      <main>
        <div className="max-w-4xl mx-auto px-4 md:px-6 pt-20">
          <Breadcrumb items={[{ label: "漫画详情" }]} />
          <div className="flex flex-col items-center justify-center py-20">
            <i className="ri-file-unknow-line text-5xl text-foreground-300 mb-4"></i>
            <p className="text-foreground-500 mb-4">未找到该漫画</p>
            <Link
              to="/"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
      </main>
    );
  }

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 6);

  const categoryName =
    {
      love: "恋爱",
      action: "热血",
      fantasy: "奇幻",
      comedy: "搞笑",
      horror: "悬疑",
      school: "校园",
      urban: "都市",
      history: "古风",
      scifi: "科幻",
    }[comic.category] || comic.category;

  return (
    <main>
      <article className="max-w-4xl mx-auto px-4 md:px-6 pt-20 pb-14">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Article",
              "headline": comic.title,
              "author": {
                "@type": "Person",
                "name": comic.author
              },
              "datePublished": comic.chapters[0]?.updateTime || comic.updateTime,
              "dateModified": comic.updateTime,
              "description": comic.description,
              "image": comic.cover,
              "publisher": {
                "@type": "Organization",
                "name": "羞羞歪歪漫画"
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "首页",
                  "item": "https://xiuxiuwaiwai.com/"
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": categoryName,
                  "item": `https://xiuxiuwaiwai.com/category/${comic.category}`
                },
                {
                  "@type": "ListItem",
                  "position": 3,
                  "name": comic.title
                }
              ]
            })
          }}
        />
        <Breadcrumb
          items={[
            { label: categoryName, href: `/category/${comic.category}` },
            { label: comic.title },
          ]}
        />

        {/* Comic Info Header */}
        <div className="flex flex-col sm:flex-row gap-6 md:gap-8 mb-10">
          <div className="flex-shrink-0 w-40 h-56 sm:w-48 sm:h-64 mx-auto sm:mx-0 rounded-lg overflow-hidden">
            <img
              src={comic.cover}
              alt={comic.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-heading text-foreground-900 mb-2">
              {comic.title}
            </h1>
            <p className="text-sm text-foreground-500 mb-3">
              作者：{comic.author}
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span
                className={`text-xs px-2.5 py-1 rounded-full font-medium whitespace-nowrap ${
                  comic.status === "连载中"
                    ? "bg-primary-100 text-primary-700"
                    : "bg-secondary-100 text-secondary-700"
                }`}
              >
                {comic.status}
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-star-fill text-accent-500"></i>
                <strong className="text-foreground-800">{comic.rating}</strong> 分
              </span>
              <span className="text-xs text-foreground-500 flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(comic.views)} 次阅读
              </span>
              <span className="text-xs text-foreground-500">
                更新于 {comic.updateTime}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5 mb-4">
              {comic.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${comic.category}`}
                  className="text-xs px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>

            <p className="text-sm text-foreground-600 leading-relaxed">
              {comic.description}
            </p>
          </div>
        </div>

        {/* Chapter List */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-heading text-foreground-900">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">
                共 {comic.chapters.length} 话
              </span>
            </h2>
            <div className="flex items-center gap-2">
              <button className="text-xs text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                正序
              </button>
              <span className="text-foreground-300">|</span>
              <button className="text-xs text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">
                倒序
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {comic.chapters.map((chapter) => (
              <div
                key={chapter.id}
                className="flex items-center justify-between px-4 py-3 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer group"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs text-primary-600 font-medium">
                      {chapter.id}
                    </span>
                  </div>
                  <span className="text-sm text-foreground-800 truncate group-hover:text-primary-500 transition-colors">
                    {chapter.title}
                  </span>
                </div>
                <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">
                  {chapter.updateTime}
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section>
            <h2 className="text-lg font-heading text-foreground-900 mb-4">
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {relatedComics.map((rc) => (
                <Link
                  key={rc.id}
                  to={`/comic/${rc.id}`}
                  className="group cursor-pointer"
                >
                  <div className="relative overflow-hidden rounded-lg aspect-[3/4] mb-2">
                    <img
                      src={rc.cover}
                      alt={rc.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 rounded-full px-2 py-0.5 flex items-center gap-1">
                      <i className="ri-star-fill text-yellow-400 text-[10px]"></i>
                      <span className="text-[10px] text-white">{rc.rating}</span>
                    </div>
                  </div>
                  <h3 className="text-xs font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                    {rc.title}
                  </h3>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </main>
  );
}